import uniqBy from 'lodash.uniqby';

import SDK from './sdk';
import {
  folderSorter,
  sortFolders,
  setupFoldersWithParents,
  buildFolderTree,
  getAllTopFolders,
  getAllVirtualResourceFolders,
  isVirtualRootFolder
} from '../helpers';
import { ACTION_TYPE } from '../helpers/consts';
import { FolderType } from '../types';

export const getActions = ({
  dispatch,
  projectId,
  includePermission
}: {
  dispatch: React.ActionDispatch<any>;
  projectId: string;
  includePermission?: boolean;
}) => ({
  setSdkLocale: (locale: string) => {
    SDK.Instance.setConfig({ locale } as any);
  },
  getTopFolders: async (folderUrn?: string, fullPath?: boolean) => {
    try {
      let folders;
      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_START
      });

      if (folderUrn) {
        // Get parent folders and top folders in parallel
        const [{ results: parents }, topFolders] = await Promise.all([
          SDK.Folders.getParentFolders(projectId, folderUrn),
          // For admin: we still need to load top folders in case For the Field is still there.
          // Once For the Field is removed, we can keep this call only for non-admin users.
          SDK.Folders.getFolders(projectId, { include_permission: includePermission })
        ]);

        // Parse and load sub folders for parent folders in path
        const folderUrns = parents.filter((folder) => folder.hasSubfolders).map((folder) => folder.urn);
        const pathFolders = await Promise.all(
          folderUrns.map((urn) => SDK.Folders.getFolder(projectId, urn, { include_permission: includePermission }))
        );
        const flatFolders = pathFolders.flatMap((folder) => folder.folders || []);
        // Merge all folders into a flat array and restructure them through setupFoldersWithParents
        folders = uniqBy([...flatFolders, ...topFolders, ...parents], 'urn');
      } else {
        folders = await SDK.Folders.getFolders(projectId, { include_permission: includePermission });
      }
      folders = setupFoldersWithParents(fullPath, folders as FolderType[]);

      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_END,
        payload: sortFolders(folders)
      });

      return folders;
    } catch (error: any) {
      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_ERROR,
        payload: error?.response?.status
      });
      throw error;
    }
  },
  getTopFoldersForFolderType: async (folderType: string, folderUrn?: string) => {
    try {
      let folders: FolderType[];
      const virtualRootFolderUrns: string[] = [];

      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_START
      });

      // Get parent folders and top folders in parallel when folderUrn is provided
      const [topFolders, parentResult] = await Promise.all([
        getAllTopFolders(
          (offset?: number) =>
            SDK.Folders.getTopFolders(projectId, { folderType, includePermission, offset } as any) as any
        ),
        folderUrn ? SDK.Folders.getParentFolders(projectId, folderUrn) : Promise.resolve(null)
      ]);

      folders = topFolders;

      if (folders.every((folder) => isVirtualRootFolder(folder))) {
        virtualRootFolderUrns.push(...folders.map((folder) => folder.urn));

        const virtualFolderResults = await Promise.all(
          folders.map((folder) =>
            getAllVirtualResourceFolders((offset?: number) =>
              SDK.VirtualFolders.getVirtualResourceFolders(projectId, folder.urn, { offset })
            )
          )
        );
        const virtualFolders = virtualFolderResults.flat();

        if (virtualFolders.length === 0) {
          throw new Error('Folder structure is incomplete');
        }

        const childResults = await Promise.all(
          virtualFolders.map(async (virtualFolder) => {
            const response = await SDK.Folders.getFolder(projectId, virtualFolder.urn, {
              include_permission: includePermission
            });
            dispatch({
              type: ACTION_TYPE.SET_HIDDEN_ROOT_FOLDER,
              payload: { hiddenFolder: virtualFolder }
            });
            return response.folders || [];
          })
        );
        folders = childResults.flat() as FolderType[];
      }

      if (folderUrn && parentResult) {
        const { results: parents } = parentResult;
        const parentsWithoutHiddenVirtualFolders = parents.filter(
          (folder) =>
            !virtualRootFolderUrns.includes(folder.urn) && !virtualRootFolderUrns.includes(folder.parentUrn || '')
        );

        // Load sub-folders for parent folders in path
        const parentUrns = parentsWithoutHiddenVirtualFolders
          .filter((folder) => folder.hasSubfolders)
          .map((folder) => folder.urn);
        const pathFolders = await Promise.all(
          parentUrns.map((urn) => SDK.Folders.getFolder(projectId, urn, { include_permission: includePermission }))
        );
        const flatFolders = pathFolders.flatMap((folder) => folder.folders || []);
        folders = buildFolderTree(
          uniqBy([...flatFolders, ...folders, ...parentsWithoutHiddenVirtualFolders], 'urn') as FolderType[]
        );
      }

      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_END,
        payload: sortFolders(folders as FolderType[])
      });

      return folders;
    } catch (error: any) {
      dispatch({
        type: ACTION_TYPE.LOAD_TOP_FOLDERS_ERROR,
        payload: error?.response?.status
      });
      throw error;
    }
  },
  getFolder: async (folderUrn: string) => {
    const response = await SDK.Folders.getFolder(projectId, folderUrn, { include_permission: includePermission });
    dispatch({
      type: ACTION_TYPE.EXPAND_FOLDER,
      payload: {
        folderUrn,
        folders: (response.folders || []).sort(folderSorter as any)
      }
    });
    return response;
  },
  getFolderPermission: async (folderUrn: string) => {
    const permissionActions = await SDK.Folders.getFolderPermission(projectId, folderUrn);
    dispatch({
      type: ACTION_TYPE.UPDATE_FOLDER,
      payload: {
        folderUrn,
        folder: { permissionActions }
      }
    });
    return permissionActions;
  },
  updateFolder: async (folderUrn: string, newTitle: string) => {
    const folder = await SDK.Folders.renameFolder(projectId, { urn: folderUrn, name: newTitle });

    // Omit the permission type here since update response will not include this info.
    // And also omit all subfolders
    delete folder.folders;
    delete folder.permissionActions;

    dispatch({
      type: ACTION_TYPE.UPDATE_FOLDER,
      payload: {
        folderUrn,
        folder
      }
    });

    return folder;
  },
  deleteFolder: async (folderUrn: string) => {
    const { results = [], errors = [] } = await SDK.Folders.deleteFolders(projectId, [folderUrn]);
    if (errors.length > 0) {
      throw new Error('Failed to delete folder');
    }

    if (results.length > 0) {
      dispatch({
        type: ACTION_TYPE.DELETE_FOLDER,
        payload: {
          folderUrn
        }
      });
    }

    return results;
  },
  createFolder: async (parentFolderUrn: string, name: string) => {
    const folder = await SDK.Folders.createFolder(projectId, {
      parentFolderUrn,
      name,
      inheritNamingStandards: true
    } as any);
    dispatch({
      type: ACTION_TYPE.CREATE_FOLDER,
      payload: {
        parentUrn: parentFolderUrn,
        folder
      }
    });
    return folder;
  },
  addPendingTreeNode: async (parentFolderUrn: string, nodeNameGenerator?: any) => {
    dispatch({
      type: ACTION_TYPE.ADD_PENDING_TREE_NODE,
      payload: {
        parentUrn: parentFolderUrn,
        nodeNameGenerator
      }
    });
  },
  removePendingTreeNode: async (parentUrn: string, folderUrn: string, dropKeys: string[]) => {
    dispatch({
      type: ACTION_TYPE.REMOVE_PENDING_TREE_NODE,
      payload: {
        parentUrn,
        folderUrn,
        dropKeys
      }
    });
  },
  updatePendingTreeNode: async (parentUrn: string, folderUrn: string, folder: object) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_PENDING_TREE_NODE,
      payload: {
        parentUrn,
        folderUrn,
        folder
      }
    });
  },
  updateTreeNode: async (folderUrn: string, folder: object, dropKeys?: string[]) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_TREE_NODE,
      payload: {
        folderUrn,
        folder,
        dropKeys
      }
    });
  },
  getCurrentUser: async () => {
    try {
      const user = await SDK.Users.getCurrentUser(projectId);
      dispatch({
        type: ACTION_TYPE.SET_USER,
        payload: user
      });
    } catch (ex) {
      console.error(`Failed to load user, skip log event.`, ex);
    }
  }
});
